import type { Entity, LineIn, Path, ProductionCreate, ProductionDetail, ProductionRow, Status } from "./api";
import { PATH_B_ONLY } from "./format";

export type DraftLine = { sku: string; qty: string; unitsPerCase: number | null };
export type ProductionForm = { entity: Entity; path: Path; factory: string; targetDate: string; productionDate: string; lines: DraftLine[] };
export const EMPTY_FORM: ProductionForm = { entity: "LTD", path: "A", factory: "", targetDate: "", productionDate: "", lines: [{ sku: "", qty: "", unitsPerCase: null }] };
const QTY = /^\d{1,14}(\.\d{1,4})?$/;

export function lineProblems(lines: DraftLine[]): Array<string | null> {
  const seen = new Map<string, number>();
  return lines.map((line, index) => {
    if (!line.sku) return "Pick a SKU.";
    if (seen.has(line.sku)) return `Duplicate of line ${seen.get(line.sku)! + 1}.`;
    seen.set(line.sku, index);
    if (!QTY.test(line.qty) || Number(line.qty) <= 0) return "Quantity must be above zero (up to 4 decimals).";
    return null;
  });
}
export function formProblems(form: ProductionForm, mode: "plan" | "start"): string[] {
  const problems: string[] = [];
  if (form.lines.length === 0) problems.push("Add at least one SKU.");
  if (mode === "plan" && !form.targetDate) problems.push("A planned production needs a target date.");
  if (mode === "start" && !form.productionDate) problems.push("Starting needs the production date.");
  if (mode === "start" && form.path === "A" && !form.factory) problems.push("A Path A production needs a factory to start.");
  if ((PATH_B_ONLY as string[]).includes(form.entity) && form.path === "A") problems.push("UAE Trading LLC buys finished goods only: use Path B.");
  return problems;
}
export function createPayload(form: ProductionForm, mode: "plan" | "start"): ProductionCreate {
  const lines: LineIn[] = form.lines.map((line) => mode === "plan" ? { sku: line.sku, planned_qty: line.qty } : { sku: line.sku, declared_qty: line.qty });
  const base: ProductionCreate = { entity_id: form.entity, path: form.path, lines, start_now: mode === "start", source: "manual" };
  if (mode === "plan") return { ...base, target_date: form.targetDate };
  return { ...base, production_date: form.productionDate, ...(form.path === "A" ? { factory_id: form.factory } : {}) };
}
export function editLinesPayload(lines: DraftLine[]): LineIn[] { return lines.map((line) => ({ sku: line.sku, planned_qty: line.qty })); }
export type RowAction = "edit" | "start" | "cancel" | "delete" | "open" | "correct";
export function rowActions(status: Status, confirmed = true): RowAction[] {
  if (status === "Planned") return ["edit", "start", confirmed ? "cancel" : "delete"];
  return status === "Posted" ? ["open", "correct"] : ["open"];
}
export function actionLabel(action: RowAction): string {
  return action === "correct" ? "Correct this production" : action[0].toUpperCase() + action.slice(1);
}
export function correctionForm(detail: ProductionDetail): ProductionForm {
  const p = detail.production;
  return {
    entity: p.entity_id, path: p.path, factory: p.factory_id ?? "", targetDate: "", productionDate: p.production_date ?? "",
    lines: detail.lines.map((line) => ({ sku: line.sku, qty: line.declared_qty ?? "", unitsPerCase: line.units_per_case })),
  };
}
export function correctionPayload(form: ProductionForm, correctsKey: string): ProductionCreate {
  return { ...createPayload(form, "start"), corrects_production_key: correctsKey };
}
export function carriedFrom(detail: Pick<ProductionDetail, "carried_pulls">, code: string): string | null {
  return (detail.carried_pulls ?? []).find((pull) => pull.xero_item_code === code)?.from_production_key ?? null;
}
export function dateFor(row: Pick<ProductionRow, "status" | "target_date" | "production_date">): string | null {
  return row.status === "Planned" ? row.target_date : row.production_date ?? row.target_date;
}
